import { toLocalDate } from '@utils';
import { TransportOrderFormValues } from './types';

/**
 * Holly v3 → Create-Transport prefill mapper.
 *
 * When a visitor gets a transport quote in the Holly chat and signs up, we land
 * them on /transport-order/create?source=holly and pre-populate the form with
 * what they already told Holly. Holly v3 persists a FLAT TransportBookingFlowState
 * (holly-v3-mcp/src/types/agents.ts) which the Koa gateway exposes verbatim via
 * /hollyv4/session/:id. This module is the single place that translates that flat
 * shape into form values (create page) and a neutral quote summary (registration
 * banner), so the two consumers can't drift.
 *
 * Partial prefill by design: we only map fields Holly reliably captures (route,
 * load, weight, timing, special requirements). Addresses and contacts are left
 * blank for the user to complete. The estimate range is summary-only — the create
 * form re-fetches firm quotes, so we never write the quote-selection fields.
 */

/** Tolerant view of the flat Holly v3 transport flowState (all fields optional). */
export interface RawHollyTransportFlowState {
  origin?: string | null;
  destination?: string | null;
  originPostcode?: string | null;
  destinationPostcode?: string | null;
  loadSize?: {
    quantity?: number | null;
    unit?: string | null;
    description?: string | null;
  } | null;
  weightKg?: number | null;
  isPalletised?: boolean | null;
  timing?: { date?: string | null; urgency?: string | null } | null;
  specialRequirements?: string[] | null;
  estimateId?: string | null;
  estimateRange?: { low?: number; high?: number; currency?: string } | null;
  // Allow unknown extra fields without widening the typed surface.
  [key: string]: unknown;
}

export interface HollyTransportQuoteSummary {
  origin?: string;
  destination?: string;
  pallets?: number | string;
  date?: string;
  estimateLow?: number;
  estimateHigh?: number;
  currency?: string;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Full UK postcode, e.g. "SK14 5RU" (outward + inward). Space optional. */
const FULL_UK_POSTCODE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

/**
 * Choose the best location string for a collection/delivery field.
 *
 * Holly stores the FULL postcode (e.g. "SK14 5RU") in `origin`/`destination`
 * but only the OUTWARD code (e.g. "SK14") in `originPostcode`/`destinationPostcode`
 * — the outward code is all its rate-card pricing needs. For the order form we
 * want the most precise value, so: prefer a complete UK postcode wherever it
 * sits, then fall back to the outward code, then a place name.
 */
function pickLocation(full?: string | null, outward?: string | null): string {
  const f = (full ?? '').trim();
  const o = (outward ?? '').trim();
  if (FULL_UK_POSTCODE.test(f)) return f;
  if (FULL_UK_POSTCODE.test(o)) return o;
  return o || f;
}

/** Returns a `yyyy-mm-dd` string if `value` parses to a valid date, else ''. */
function normaliseDate(value?: string | null): string {
  if (!value) return '';
  if (ISO_DATE.test(value)) return value;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return toLocalDate(parsed);
}

/**
 * Map Holly's free-form specialRequirements tokens onto the form's known boolean
 * flags. Anything we don't recognise is returned in `leftovers` so the caller can
 * surface it (e.g. appended to the collection notes) rather than dropping it.
 */
function mapSpecialRequirements(requirements: string[]): {
  flags: Partial<
    Pick<
      TransportOrderFormValues,
      'tailLiftRequired' | 'moffettRequired' | 'fragileHandling'
    >
  >;
  leftovers: string[];
} {
  const flags: Partial<
    Pick<
      TransportOrderFormValues,
      'tailLiftRequired' | 'moffettRequired' | 'fragileHandling'
    >
  > = {};
  const leftovers: string[] = [];

  for (const raw of requirements) {
    if (!raw || typeof raw !== 'string') continue;
    const token = raw.toLowerCase();
    if (token.includes('tail lift') || token.includes('taillift')) {
      flags.tailLiftRequired = true;
    } else if (token.includes('moffett')) {
      flags.moffettRequired = true;
    } else if (token.includes('fragile')) {
      flags.fragileHandling = true;
    } else {
      leftovers.push(raw.trim());
    }
  }

  return { flags, leftovers };
}

/**
 * Translate the flat Holly v3 transport flowState into a partial set of form
 * values. Only keys with a real value are returned, so React Hook Form's
 * defaultValues fill in the rest.
 */
export function toTransportOrderPrefill(
  flowState?: RawHollyTransportFlowState | null
): Partial<TransportOrderFormValues> {
  if (!flowState || typeof flowState !== 'object') return {};

  const prefill: Partial<TransportOrderFormValues> = {};

  // Holly v3 keeps the FULL postcode in `origin`/`destination` and only the
  // OUTWARD code in the `*Postcode` fields (which is all its pricing needs).
  // `pickLocation` prefers the complete postcode so the form isn't pre-filled
  // with a truncated outward code like "SK14". The form's collection/delivery
  // inputs accept a "postcode or place" either way.
  const originLocation = pickLocation(flowState.origin, flowState.originPostcode);
  if (originLocation) {
    prefill.collectionPostcode = originLocation;
    prefill.pickupPostcode = originLocation;
  }
  const destinationLocation = pickLocation(
    flowState.destination,
    flowState.destinationPostcode
  );
  if (destinationLocation) {
    prefill.deliveryPostcode = destinationLocation;
    prefill.dropoffPostcode = destinationLocation;
  }

  const load = flowState.loadSize;
  const isPalletUnit =
    typeof load?.unit === 'string' && load.unit.toLowerCase().includes('pallet');

  if (load && isPalletUnit && typeof load.quantity === 'number') {
    prefill.totalPallets = load.quantity;
  }

  if (flowState.isPalletised === true) {
    prefill.palletType = 'GB Pallet';
  } else if (flowState.isPalletised === false) {
    prefill.palletType = 'Unpalletised';
  }

  if (typeof flowState.weightKg === 'number') {
    prefill.weight = flowState.weightKg;
    prefill.weightUnit = 'Kg';
  }

  // Build a human-readable load summary for briefContents.
  if (load?.description) {
    prefill.briefContents = load.description;
  } else if (load && !isPalletUnit && typeof load.quantity === 'number' && load.unit) {
    prefill.briefContents = `${load.quantity} ${load.unit}`;
  }

  const collectionDate = normaliseDate(flowState.timing?.date);
  if (collectionDate) {
    prefill.collectionDate = collectionDate;
  }

  if (Array.isArray(flowState.specialRequirements)) {
    const { flags, leftovers } = mapSpecialRequirements(
      flowState.specialRequirements
    );
    Object.assign(prefill, flags);
    if (leftovers.length > 0) {
      prefill.pickupAdditionalNotes = leftovers.join(', ');
    }
  }

  return prefill;
}

/**
 * Neutral summary used by the registration "Your quote from Holly" banner. Returns
 * null when there's nothing worth showing.
 */
export function toTransportQuoteSummary(
  flowState?: RawHollyTransportFlowState | null
): HollyTransportQuoteSummary | null {
  if (!flowState || typeof flowState !== 'object') return null;

  const summary: HollyTransportQuoteSummary = {};

  if (flowState.origin) summary.origin = flowState.origin;
  if (flowState.destination) summary.destination = flowState.destination;

  const load = flowState.loadSize;
  if (load && typeof load.quantity === 'number') {
    summary.pallets = load.quantity;
  }

  const date = normaliseDate(flowState.timing?.date);
  if (date) summary.date = date;

  const range = flowState.estimateRange;
  if (range && typeof range.low === 'number' && typeof range.high === 'number') {
    summary.estimateLow = range.low;
    summary.estimateHigh = range.high;
    summary.currency = range.currency;
  }

  return Object.keys(summary).length > 0 ? summary : null;
}
